import type { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { logError } from '@rover/react-lib/src/utils/logError';
import {
  apiFrontendServiceLandingLocaleFaqsList,
  getApiFrontendServiceLandingLocaleFaqsListQueryKey,
  useApiFrontendServiceLandingLocaleFaqsList,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-service-landing-locale-faqs-list';
import type { LandingFAQ } from '@rover/rsdk/src/apiClient/latest/model/landingFAQ';
import type { ServiceLandingPathDescriptor } from '@rover/shared/js/hooks/useServiceLanding';

const faqsArgs = ({
  countryUrlPrefix,
  language,
  serviceLandingSlug,
}: ServiceLandingPathDescriptor) => [countryUrlPrefix, language, serviceLandingSlug] as const;

/**
 * The page's FAQs, or `undefined` while loading or on error, so a failed request renders no FAQ
 * section rather than failing the page.
 */
export const useServiceLandingFaqs = (
  descriptor: ServiceLandingPathDescriptor
): LandingFAQ[] | undefined =>
  useApiFrontendServiceLandingLocaleFaqsList(...faqsArgs(descriptor)).data;

/**
 * A failed prefetch stays an errored query, which isn't dehydrated, so the client fetches the
 * FAQs again on mount. Retrying on the server would only stall the render first.
 */
export const getServiceLandingFaqsPrefetchQueryOptions = (
  descriptor: ServiceLandingPathDescriptor,
  locale: Locale
): {
  queryKey: ReturnType<typeof getApiFrontendServiceLandingLocaleFaqsListQueryKey>;
  queryFn: () => Promise<LandingFAQ[]>;
  retry: false;
} => {
  const args = faqsArgs(descriptor);
  return {
    queryKey: getApiFrontendServiceLandingLocaleFaqsListQueryKey(...args),
    queryFn: () =>
      apiFrontendServiceLandingLocaleFaqsList(...args).catch((error: Error) => {
        const { countryUrlPrefix, serviceLandingSlug } = descriptor;
        logError(new Error(`Failed to prefetch service landing FAQs: ${error.message}`), {
          countryUrlPrefix,
          locale,
          serviceLandingSlug,
        });
        throw error;
      }),
    retry: false,
  };
};
